// Toasts (spec/dashboard.md section 2.2): a short confirmation, info, warning or error in the corner. A toast may carry up
// to two next-step actions (View commit, Build release…); clicking one runs it and dismisses the toast. A toast with
// actions stays 12 s instead of 6 (it has something to click); errors stay until dismissed.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, IconButton } from './Button';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from './icons';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info' | 'warning';
  message: string;
  requestId?: string | null;
  detail?: string;
  /** at most two next steps, rendered as small ghost buttons under the message */
  actions?: ToastAction[];
}

export interface ToastExtra {
  actions?: ToastAction[];
}

export interface Toasts {
  success: (message: string, extra?: ToastExtra) => void;
  info: (message: string, extra?: ToastExtra) => void;
  /** a non-blocking warning of a write (X-Huelency-Warning: a near-duplicate template name, Q-D-27) */
  warning: (message: string, extra?: ToastExtra) => void;
  error: (message: string, extra?: { requestId?: string | null; detail?: string; actions?: ToastAction[] }) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<Toasts | null>(null);
const toneIcon = { success: CheckCircle2, error: AlertCircle, info: Info, warning: AlertTriangle } as const;
const MAX_ACTIONS = 2;
/** a toast with actions stays this many times longer than a plain one */
const ACTION_FACTOR = 2;

export function ToastProvider({ children, autoDismissMs = 6000 }: { children: ReactNode; autoDismissMs?: number }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = ++counter.current;
      const actions = toast.actions?.length ? toast.actions.slice(0, MAX_ACTIONS) : undefined;
      setToasts((t) => [...t, { ...toast, actions, id }]);
      if (autoDismissMs > 0 && toast.tone !== 'error') setTimeout(() => dismiss(id), actions ? autoDismissMs * ACTION_FACTOR : autoDismissMs);
    },
    [autoDismissMs, dismiss],
  );
  const api = useMemo<Toasts>(
    () => ({
      success: (message, extra) => push({ tone: 'success', message, actions: extra?.actions }),
      info: (message, extra) => push({ tone: 'info', message, actions: extra?.actions }),
      warning: (message, extra) => push({ tone: 'warning', message, actions: extra?.actions }),
      error: (message, extra) => push({ tone: 'error', message, requestId: extra?.requestId ?? null, detail: extra?.detail, actions: extra?.actions }),
      dismiss,
    }),
    [push, dismiss],
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => {
          const Icon = toneIcon[t.tone];
          return (
            <div key={t.id} className={`toast toast-${t.tone}`} data-tone={t.tone}>
              <Icon className="toast-icon" aria-hidden="true" />
              <div className="toast-body">
                <div>{t.message}</div>
                {t.detail && <div className="muted">{t.detail}</div>}
                {t.requestId && <div className="muted">request {t.requestId}</div>}
                {t.actions && (
                  <div className="toast-actions">
                    {t.actions.map((a) => (
                      <Button
                        key={a.label}
                        size="s"
                        variant="ghost"
                        onClick={() => {
                          dismiss(t.id);
                          a.onClick();
                        }}
                      >
                        {a.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <IconButton label="Dismiss" onClick={() => dismiss(t.id)}>
                <X aria-hidden="true" />
              </IconButton>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToasts(): Toasts {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToasts must be used inside ToastProvider');
  return ctx;
}
